import React, { useEffect, useMemo, useState } from "react";
import { AutoComplete, Button, DatePicker, Select, SelectProps } from "antd";
import { useNavigate } from "react-router-dom";
import ListApi from "../../../../api-services/list-api.service";
import { monthFormat } from "../../../../utils/antd.props";
import { EMPTY_FILTERS, ListingFilterState, toQueryString } from "../../../../utils/listingSearchParams";
import type { TravelMode } from "../Homepage";

const ICONS = "/iceholidays-assets/images/icons";

interface Props {
  mode: TravelMode;
  onModeChange: (mode: TravelMode) => void;
}

export default function HeroSearch({ mode, onModeChange }: Props) {
  const navigate = useNavigate();
  const listApi = useMemo(() => new ListApi(), []);

  const [destinations, setDestinations] = useState<SelectProps["options"]>([]);
  const [exEnds, setExEnds] = useState<SelectProps["options"]>([]);
  const [brands, setBrands] = useState<SelectProps["options"]>([]);
  const [draft, setDraft] = useState<ListingFilterState>(EMPTY_FILTERS);

  useEffect(() => {
    let active = true;
    const fetchItineraries =
      mode === "cruise" ? listApi.getCruiseItineraries() : listApi.getItineraries();

    Promise.all([listApi.getLocations(), fetchItineraries])
      .then(([locations, codes]) => {
        if (!active) return;
        const options: SelectProps["options"] = [];
        if (locations.length) {
          options.push({
            label: <strong> Destinations </strong>,
            options: locations.map(({ id, name }: any) => ({ key: id, value: name, label: name })),
          });
        }
        if (codes.length) {
          options.push({
            label: <strong> Itineraries </strong>,
            options: codes.map(({ id, name }: any) => ({ key: id, value: name, label: name })),
          });
        }
        setDestinations(options);
      })
      .catch(() => {});

    return () => {
      active = false;
    };
  }, [listApi, mode]);

  useEffect(() => {
    let active = true;

    listApi.getExEndOptions().then((options) => active && setExEnds(options)).catch(() => {});
    listApi
      .getBrands()
      .then((groups: any[]) => {
        if (!active) return;
        const activeType = mode === "cruise" ? "cruise" : "series";
        setBrands(groups.find((group) => group.type === activeType)?.options ?? []);
      })
      .catch(() => {});

    return () => {
      active = false;
    };
  }, [listApi, mode]);

  const search = () => {
    const query = toQueryString({ ...draft, type: mode === "cruise" ? "cruise" : null });
    navigate(`/web/listing${query ? `?${query}` : ""}`);
  };

  return (
    <section id="hero-v2">
      <div className="hero-v2__overlay">
        <div className="hero-v2__copy">
          <h1>Travel Further. Experience More.</h1>
          <p>Exclusive tours, cruises and holidays with Malaysia's trusted travel specialist.</p>
        </div>

        <div className="hero-v2__toggle">
          <button
            className={mode === "tour" ? "is-active" : ""}
            onClick={() => onModeChange("tour")}
            type="button"
          >
            <img src={`${ICONS}/icon-toggle-tour.png`} alt="" aria-hidden="true" /> Tour
          </button>
          <button
            className={mode === "cruise" ? "is-active" : ""}
            onClick={() => onModeChange("cruise")}
            type="button"
          >
            <img src={`${ICONS}/icon-toggle-cruise.png`} alt="" aria-hidden="true" /> Cruise
          </button>
        </div>

        <div className="hero-v2__search">
          <div className="hero-v2__field">
            <img src={`${ICONS}/icon-search-location.png`} alt="" aria-hidden="true" />
            <AutoComplete
              variant="borderless"
              allowClear
              filterOption
              placeholder="Where would you like to go?"
              options={destinations}
              value={draft.keyword || undefined}
              onChange={(value) => setDraft({ ...draft, keyword: value || "" })}
            />
          </div>
          <div className="hero-v2__field hero-v2__field--depart">
            <img src={`${ICONS}/icon-search-city.png`} alt="" aria-hidden="true" />
            <Select
              variant="borderless"
              allowClear
              showSearch
              placeholder="Departure City"
              options={exEnds}
              optionFilterProp="searchText"
              popupMatchSelectWidth={false}
              suffixIcon={null}
              value={draft.exEnd[0]}
              onChange={(value) => setDraft({ ...draft, exEnd: value ? [value] : [] })}
            />
          </div>
          <div className="hero-v2__field">
            <img src={`${ICONS}/icon-search-date.png`} alt="" aria-hidden="true" />
            <DatePicker
              variant="borderless"
              placeholder="Travel Date"
              picker="month"
              format={monthFormat}
              inputReadOnly
              suffixIcon={null}
              onChange={(value) => setDraft({ ...draft, date: value ? value.format("YYYY-MM-01") : null })}
            />
          </div>
          <div className="hero-v2__field">
            <img src={`${ICONS}/icon-search-brand.png`} alt="" aria-hidden="true" />
            <Select
              variant="borderless"
              mode="multiple"
              allowClear
              placeholder="Brand"
              options={brands}
              popupMatchSelectWidth={false}
              maxTagCount={1}
              suffixIcon={null}
              value={draft.brands}
              onChange={(value) => setDraft({ ...draft, brands: value })}
            />
          </div>
          <Button type="primary" className="hero-v2__search-btn" onClick={search}>
            Search Holidays
          </Button>
        </div>
      </div>
    </section>
  );
}
